import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { QuestionnaireTemplate, ResponseItem } from '@app/src/types/resourceExplorer'
import paths from '@app/src/wf-constants/paths'
import type { FindingsListRowModel } from './FindingsList/FindingsListScene'

export function buildReportingUrl(requestId?: number | null, responseId?: number | null): string | null {
  if (!requestId || !responseId) return null
  const qs = new URLSearchParams({
    requestId: String(requestId),
    responseId: String(responseId),
  })
  return `${paths.reporting}?${qs.toString()}`
}

const RESPONSE_ITEM_FILTER_PATH: Record<string, string> = {
  'provider.id': 'response.request.subscriptions.target.id',
  questionnaireTemplateId: 'response.request.questionnaireTemplateId',
}

export function mapUserFiltersToResponseItemFilters(filters: FilterGroup[]): FilterGroup[] {
  return filters.map(f => ({
    ...f,
    name: RESPONSE_ITEM_FILTER_PATH[f.name] ?? f.name,
  }))
}

export type AdverseScreeningQuestionnaire = string
const SCREENING_TAG_PREFIX = 'adverse-screening:'

const KNOWN_SCREENING_ORDER = ['adverse-media', 'sanctions', 'pep', 'greenwashing']

const getTagScreeningSlug = (tags: readonly string[] | undefined): string | undefined =>
  tags?.find(tag => tag.startsWith(SCREENING_TAG_PREFIX))?.slice(SCREENING_TAG_PREFIX.length) || undefined

export function getTemplateScreeningSlug(
  template: Pick<QuestionnaireTemplate, 'tags'>,
): AdverseScreeningQuestionnaire | undefined {
  return getTagScreeningSlug(template.tags)
}

export function getScreeningSlugByTitle(
  title: string,
  templates: QuestionnaireTemplate[],
): AdverseScreeningQuestionnaire | undefined {
  const match = templates.find(t => t.title === title)
  return match ? getTemplateScreeningSlug(match) : undefined
}

const toTranslationSegment = (slug: string): string =>
  slug.replace(/-([a-z0-9])/g, (_, char: string) => char.toUpperCase())

export const getScreeningLabelId = (slug: string): string => `findings.screenings.${toTranslationSegment(slug)}`

export const getScreeningTitleId = (slug: string): string =>
  `findings.aiScreeningModal.adverseScreeningQuestionnaires.${toTranslationSegment(slug)}.title`

export const getScreeningDescriptionId = (slug: string): string =>
  `findings.aiScreeningModal.adverseScreeningQuestionnaires.${toTranslationSegment(slug)}.description`

function screeningPriority(slug: string): number {
  const index = KNOWN_SCREENING_ORDER.indexOf(slug)
  return index === -1 ? KNOWN_SCREENING_ORDER.length : index
}

export function toScreeningTemplates(templates: QuestionnaireTemplate[]): QuestionnaireTemplate[] {
  return [...templates].sort((a, b) => {
    const slugA = getTemplateScreeningSlug(a) ?? ''
    const slugB = getTemplateScreeningSlug(b) ?? ''
    return screeningPriority(slugA) - screeningPriority(slugB) || a.title.localeCompare(b.title)
  })
}

export function getScreeningTemplateIds(
  templates: QuestionnaireTemplate[],
  type: AdverseScreeningQuestionnaire,
): number[] {
  return templates.filter(t => getTemplateScreeningSlug(t) === type).map(t => t.id)
}

export interface ScreeningColumn {
  type: AdverseScreeningQuestionnaire
  labelId: string
  titleId: string
  descriptionId: string
  fallbackLabel: string
  templateIds: number[]
  totalQuestions: number
  description: string | null
}

function orderScreeningSlugs(slugs: string[]): string[] {
  const known = KNOWN_SCREENING_ORDER.filter(slug => slugs.includes(slug))
  const extra = slugs.filter(slug => !KNOWN_SCREENING_ORDER.includes(slug)).sort()
  return [...known, ...extra]
}

export function getScreeningColumns(templates: QuestionnaireTemplate[]): ScreeningColumn[] {
  const templatesBySlug = new Map<string, QuestionnaireTemplate[]>()
  for (const template of templates) {
    const slug = getTemplateScreeningSlug(template)
    if (!slug) continue
    const existing = templatesBySlug.get(slug) ?? []
    existing.push(template)
    templatesBySlug.set(slug, existing)
  }

  return orderScreeningSlugs([...templatesBySlug.keys()]).map(slug => {
    const typeTemplates = templatesBySlug.get(slug) ?? []
    const totalQuestions = typeTemplates.reduce(
      (acc, t) => acc + (t.sections?.reduce((sum, s) => sum + (s.questions?.length ?? 0), 0) ?? 0),
      0,
    )
    return {
      type: slug,
      labelId: getScreeningLabelId(slug),
      titleId: getScreeningTitleId(slug),
      descriptionId: getScreeningDescriptionId(slug),
      fallbackLabel: typeTemplates[0]?.title ?? slug,
      templateIds: typeTemplates.map(t => t.id),
      totalQuestions,
      description: typeTemplates[0]?.description ?? null,
    }
  })
}

export function getColumnFindingsCount(
  countsByTemplateId: Record<number, number> | undefined,
  column: ScreeningColumn,
): number {
  return column.templateIds.reduce((acc, id) => acc + (countsByTemplateId?.[id] ?? 0), 0)
}

export function isColumnScreened(screenedTemplateIds: number[] | undefined, column: ScreeningColumn): boolean {
  return Boolean(screenedTemplateIds?.some(id => column.templateIds.includes(id)))
}

export function toFindingsListRow(item: ResponseItem): FindingsListRowModel {
  const question = item.requestItem?.template
  const findingDisplayName = question?.kpiName || question?.questionText || ''
  const request = item.response?.request
  const provider = request?.targetAliasObject as FindingsListRowModel['provider']
  return {
    findingDisplayName,
    questionnaireTemplateTitle: request?.template?.title ?? null,
    provider,
    date: item.response?.submittedAt ?? item.createdAt,
    responseItemId: item.id ?? null,
  }
}
